import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { MAX_TRIMS_COMPARE, CLICK_EVENT_TRIGGER, UI_CONTROLS_ACTION_CATEGORY, FAC_EVENT_ACTION_TYPE, FAC_MODULE_NAME, FAC_FOOTER_COMPARE_MODULE, FAC_TRIM_ID_LIST_PREFIX_KEY } from '../../../../../constants';
import { generateImage, trackingEvent } from '../../../../../utils';
import TooltipMsrp from '../../TooltipMsrp';
import GlobalLoading from '../loading/GlobalLoading';
import { getDefaultComparisonModel } from '../../../../../utils/tracking';

const CompareFooter = ({
  labels,
  selectedTrims,
  isDifferentSegment,
  handleRemoveTrim,
  handleCompare,
  handleClear,
  updateSelectedTrimsUrlParams,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [localSelectedTrims, setLocalSelectedTrims] = useState([]);

  const getLocalSelectedTrims = (prevSelectedTrims, prevLocalSelectedTrims) => {
    const newLocalSelectedTrims = [...prevLocalSelectedTrims];
    prevSelectedTrims.forEach((trim) => {
      if (!trim.modelId) return;

      const existingIndex = newLocalSelectedTrims.findIndex((localTrim) => localTrim.modelId === trim.modelId);

      if (existingIndex !== -1) {
        newLocalSelectedTrims[existingIndex] = trim;
      } else {
        const emptySlotIndex = newLocalSelectedTrims.findIndex((localTrim) => !localTrim.modelId);
        if (emptySlotIndex !== -1) {
          newLocalSelectedTrims[emptySlotIndex] = trim;
        } else if (newLocalSelectedTrims.length < MAX_TRIMS_COMPARE) {
          newLocalSelectedTrims.push(trim);
        }
      }
    });
    return newLocalSelectedTrims;
  };

  useEffect(() => {
    setLocalSelectedTrims((prevLocalSelectedTrims) => {
      const newLocalSelectedTrims = getLocalSelectedTrims(selectedTrims, prevLocalSelectedTrims);
      if (updateSelectedTrimsUrlParams && typeof updateSelectedTrimsUrlParams === 'function') {
        updateSelectedTrimsUrlParams(newLocalSelectedTrims.filter((trim) => trim.modelId));
      }
      return newLocalSelectedTrims;
    });
  }, [selectedTrims, updateSelectedTrimsUrlParams]);

  useEffect(() => {
    if (isDifferentSegment) {
      setLocalSelectedTrims((prev) => prev.filter((item) => !item.loading));
    }
  }, [isDifferentSegment]);

  const handleRemoveSelectedTrim = (modelId) => {
    const removeIndex = localSelectedTrims.findIndex((trim) => trim.modelId === modelId);
    const removeTrim = localSelectedTrims[removeIndex];
    if (removeIndex !== -1) {
      const newLocalSelectedTrims = [...localSelectedTrims];
      newLocalSelectedTrims[removeIndex] = {};
      setLocalSelectedTrims(newLocalSelectedTrims);
      handleRemoveTrim(modelId);
      trackingEvent({
        event: CLICK_EVENT_TRIGGER,
        type: FAC_EVENT_ACTION_TYPE,
        label: `${FAC_MODULE_NAME}:queue:remove from queue`,
        category: UI_CONTROLS_ACTION_CATEGORY,
        custom: {
          'link_metadata': {
            'destination_url': '',
          },
          metadata: {
            'target_url': '',
          },
          compare: getDefaultComparisonModel(newLocalSelectedTrims.filter((trim) => trim.modelId), FAC_TRIM_ID_LIST_PREFIX_KEY.QUEUE),
          model: {
            'body_style': removeTrim.vehicleType,
            'model_bodystyle': removeTrim.category,
            'model_name': removeTrim.family,
            'model_id': removeTrim.modelId,
            'model_trim': removeTrim.name,
            'model_year': String(removeTrim.year),
          },
        },
        isClearCustom: true,
      });
    }
  };

  const handleCompareTrims = (e) => {
    handleCompare(e);
    trackingEvent({
      event: CLICK_EVENT_TRIGGER,
      type: FAC_EVENT_ACTION_TYPE,
      label: `${FAC_MODULE_NAME}:${FAC_FOOTER_COMPARE_MODULE}:${labels.footer.compare.replace('{{count}}', selectedTrims.length)}`,
      category: UI_CONTROLS_ACTION_CATEGORY,
      custom: {
        'link_metadata': {
          'destination_url': '',
        },
        metadata: {
          'target_url': '',
        },
        compare: getDefaultComparisonModel(localSelectedTrims.filter((trim) => trim.modelId), FAC_TRIM_ID_LIST_PREFIX_KEY.INITIAL),
      },
      isClearCustom: true,
    });
  };

  const getCompareFooterClasses = (isExpanded2, selectedTrims2) => {
    const baseClasses = [
      'flex -mx-4 xl:mx-12 md:min-w-34',
      'overflow-hidden',
      'xl:border-r-1 xl:border-gray-300',
      'w-auto md:mr-0 md:w-full',
      'transition-all duration-300 ease-in-out',
      'xl:w-51p',
    ];

    const expandedClasses = isExpanded2
      ? 'md:opacity-100 md:max-h-92 xl:opacity-100 xl:max-h-none'
      : 'xl:opacity-100 xl:max-h-none overflow-hidden hidden-compare-footer';

    const trimClasses = {
      1: 'one-col-compare',
      2: 'two-col-compare down_md:justify-center',
      3: 'three-col-compare overflow-x-auto -mr-12',
    }[selectedTrims2.length] || '';

    return [...baseClasses, expandedClasses, trimClasses].filter(Boolean).join(' ');
  };

  const getMsrpStringHtml = useCallback((trim) => (
    <span className="block text-left overflow-hidden text-ellipsis">
      {labels.baseMsrp}&nbsp;{trim.msrp}*
      {trim.destinationCharge && (
        <span className="block overflow-hidden text-ellipsis">
          {labels.destinationCharge} {trim.destinationCharge}
        </span>
      )}
    </span>
  ), [labels.baseMsrp, labels.destinationCharge]);

  return ReactDOM.createPortal(
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t shadow-pin-compare z-9999">
      <div className="container">
        <div className="md:flex down_xl:flex-wrap items-center justify-center xl:justify-between">
          <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="down_md:block down_md:mx-auto text-lg xl:text-xl font-bold pt-6 pb-11 md:py-8 xl:py-0 xl:w-22p text-center">
            {labels.footer.headline}
            <span
              className="font-bold text-base inline-block xl:hidden relative top-2 ml-3"
            >
              <span className={`icomoon text-2xl inline-block transition-transform duration-300 ${
                isExpanded ? 'icon-chevron-down' : 'icon-chevron-down rotation-180'
              }`} aria-hidden="true" />
              <span className="sr-only">{labels.footer.expand}</span>
            </span>
          </button>
          <div className={getCompareFooterClasses(isExpanded, localSelectedTrims)}>
            {/* Render selected motorcycles */}
            {localSelectedTrims.map((trim, index) => (
              <div key={`compare-footer-trim-${trim.modelId || index}`} className="compare-footer-trim-item flex-none w-50p md:w-33p px-8 border-r-1 xl:border-r-0 xl:border-l-1 border-gray-300 pt-2 pb-8 relative">
                {trim.loading && <GlobalLoading show={trim.loading} middleAlignment/>}
                {!trim.loading && trim.modelId && (
                  <>
                    <div className="flex flex-col items-center text-center">
                      <img src={generateImage(trim.productImage)} alt={trim.name} className={`w-75 px-6 transition-all duration-300 ease-in-out ${!isExpanded ? 'mb-0' : 'mb-9 xl:mb-12 '}`} loading="lazy" />
                      <div className={`group-content text-left w-full transition-all duration-300 ease-in-out ${!isExpanded ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-40'}`}>
                        <div className="text-base font-bold whitespace-nowrap overflow-hidden text-ellipsis">{trim.year} {trim.name}</div>
                        <div className="relative">
                          <TooltipMsrp className="text-gray-650 text-sm w-full whitespace-nowrap overflow-hidden text-ellipsis" position="top" btnText={getMsrpStringHtml(trim)} msrp={`*${trim.msrpDisclaimer}`} />
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="absolute top-5 w-10 h-10 flex items-center justify-center right-5 border-[1.5px] border-gray-650 text-gray-650 rounded-full p-1 hover:text-white hover:border-primary-100 hover:bg-primary-100 transition-all duration-300 ease-in-out"
                      aria-label="Remove model"
                      onClick={() => handleRemoveSelectedTrim(trim.modelId)}
                    >
                      <span className="icomoon icon-x text-font-10 font-bold" aria-hidden="true" />
                    </button>
                  </>
                )}
                {!trim.loading && !trim.modelId && (
                  <div className="flex-1 compare-footer-add-trim md:px-8 md:border-l-1 hidden md:flex justify-center text-center text-h3 font-semibold items-center border-gray-300 py-2 text-gray-300">
                    {index + 1}
                  </div>
                )}
              </div>
            ))}
            {/* Add Motorcycle placeholders */}
            {Array.from({ length: MAX_TRIMS_COMPARE - localSelectedTrims.length }).map((_, index) => (
              <div key={`empty-${index}`} className="flex-1 md:px-8 md:border-l-1 hidden md:flex justify-center text-center text-h3 font-semibold items-center border-gray-300 py-2 text-gray-300">
                {localSelectedTrims.length + index + 1}
              </div>
            ))}
          </div>

          <div className={`flex flex-col md:flex-row xl:flex-col items-center gap-2 relative xl:py-0 xl:w-24p text-center transition-all duration-300 ease-in-out ${ !isExpanded ? 'opacity-0 xl:opacity-100 max-h-0 xl:max-h-none py-0 overflow-hidden xl:overflow-visible' : 'py-8 opacity-100 max-h-79 xl:opacity-100 xl:max-h-none'}`}>
            <button
              type="button"
              className={`btn btn-ani btn-primary down_md:mr-0 w-full max-w-370 xl:w-auto xl:-ml-13 ${!isExpanded ? 'mb-0' : 'mb-6 md:mb-0 xl:mb-6'}`}
              onClick={handleCompareTrims}
            >
              {labels.footer.compare.replace('{{count}}', selectedTrims.length)}
              <span className="relative overflow-hidden inline-flex">
                <span className="icomoon icon-arrow-lr-animation" aria-hidden="true"><span className="path1 inline-block" /><span className="inline-block path2" /></span>
                <span className="icomoon icon-arrow-lr-animation absolute" aria-hidden="true"><span className="path1 inline-block relative -right-11"/><span className="inline-block path2 relative -left-11"/></span>
              </span>
            </button>
            <button
              type="button"
              className={`font-bold text-sm hover:underline transition-all duration-300 ease-in-out xl:-ml-18 ${
                !isExpanded ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-10'
              }`}
              onClick={handleClear}
              data-event-trigger={CLICK_EVENT_TRIGGER}
              data-event-type={FAC_EVENT_ACTION_TYPE}
              data-event-module={FAC_FOOTER_COMPARE_MODULE}
              data-event-label={labels.footer.clear}
              data-event-category={UI_CONTROLS_ACTION_CATEGORY}
              data-event-page={FAC_MODULE_NAME}
            >
              {labels.footer.clear}
            </button>
            <button
              type="button"
              className="font-bold text-base absolute transform-middle right-7 hidden xl:block"
              onClick={() => setIsExpanded(!isExpanded)}
              data-event-trigger={CLICK_EVENT_TRIGGER}
              data-event-type={FAC_EVENT_ACTION_TYPE}
              data-event-module="queue"
              data-event-label={isExpanded ? 'collapse' : 'expand'}
              data-event-category={UI_CONTROLS_ACTION_CATEGORY}
              data-event-page={FAC_MODULE_NAME}
            >
              <span className={`icomoon text-3xl inline-block transition-transform duration-300 ${
                isExpanded ? 'icon-chevron-down' : 'icon-chevron-down rotation-180'
              }`} aria-hidden="true" />
              <span className="sr-only">{labels.footer.expand}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CompareFooter;